import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLabels } from 'wsm-common-data';
import { usePaginationContext } from '../../contexts/PaginationContext';

const PaginationButton = ({ direction, onNavigate }) => {
	const labels = useLabels();
	const buttonRef = useRef(null);
	const isNext = direction === 'next';
	const { offset, itemsPerPage, canGoNext, canGoPrevious } =
		usePaginationContext();
	const currentPage = Math.floor(offset / itemsPerPage) + 1;

	const isDisabled = isNext ? !canGoNext : !canGoPrevious;

	const targetPage = isNext ? currentPage + 1 : currentPage - 1;

	// Blur button when it becomes disabled while focused
	useEffect(() => {
		if (isDisabled && buttonRef.current === document.activeElement) {
			buttonRef.current.blur();
		}
	}, [isDisabled]);

	const handleClick = (e) => {
		e.preventDefault();
		if (!isDisabled && onNavigate) {
			onNavigate();
		}
	};

	const iconClass = isNext
		? 'ddc-icon ddc-icon-skinny-arrow-right'
		: 'ddc-icon ddc-icon-skinny-arrow-left';

	const label = `${labels.get(
		isNext ? 'GO_TO_NEXT_PAGE' : 'GO_TO_PREVIOUS_PAGE'
	)} (${targetPage})`;

	const unavailableLabelId = isNext
		? 'NEXT_PAGE_NOT_AVAILABLE'
		: 'PREVIOUS_PAGE_NOT_AVAILABLE';
	const availableLabelId = isNext
		? 'VIEW_NEXT_PAGE_OF_MATCHING_RESULTS'
		: 'VIEW_PREVIOUS_PAGE_OF_MATCHING_RESULTS';

	const title = labels.get(
		isDisabled ? unavailableLabelId : availableLabelId
	);

	const className = `mx-3 pagination-${isNext ? 'next' : 'previous'}${
		isDisabled ? ' disabled' : ''
	}`;

	return (
		<li className={className}>
			{/* To use existing css we are using anchor tag taking reference from kinetic style guide */}
			{/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
			<a
				ref={buttonRef}
				aria-label={label}
				aria-disabled={isDisabled}
				onClick={handleClick}
				title={title}
				className={isDisabled ? 'disabled' : ''}
				href="#"
				tabIndex={isDisabled ? -1 : 0}
			>
				<i className={iconClass} aria-hidden="true" />
			</a>
		</li>
	);
};

PaginationButton.propTypes = {
	direction: PropTypes.oneOf(['next', 'previous']).isRequired,
	onNavigate: PropTypes.func.isRequired
};

export default PaginationButton;
